import React, { useMemo } from 'react';
import { I18nReacher, I18nFormatArgs, Plan, SubscriptionPlanDTO } from '@springbig/shared';
import { Caption, H4 } from '~/shared/Typography';
import { format, isBefore } from 'date-fns';

type Schedule = {
    text: I18nReacher,
    textValues: I18nFormatArgs,
    prefix_text?: I18nReacher,
    prefix_textValues?: I18nFormatArgs,
};

type Billing = {
    both: Schedule,
    monthly: Schedule,
    yearly: Schedule,
};

export type PlanScheduleProps = {
    plan: Plan | SubscriptionPlanDTO;
    fontWeight?: string;
    fontSize?: number;
};

export const PlanSchedule = ({
    plan,
    fontWeight = 'bold',
    fontSize = 14,
}: PlanScheduleProps) => {

    const generateSchedule = (): Schedule => {

        let result: Schedule;
        if (!plan.subscription) {
            const billing: Billing = {
                both: {
                    text: 'subscriptions.billing.both',
                    textValues: [plan.recurring_amount.toFixed(2), plan.yearly_recurring_amount.toFixed(2)],
                    prefix_text: plan.trial_period > 0 ? 'subscriptions.free_then' : null,
                    prefix_textValues: plan.trial_period > 0 ? [plan.trial_period] : [],
                },
                monthly: {
                    text: 'subscriptions.billing.monthly',
                    textValues: [plan.recurring_amount.toFixed(2)],
                    prefix_text: plan.trial_period > 0 ? 'subscriptions.free_then' : null,
                    prefix_textValues: plan.trial_period > 0 ? [plan.trial_period] : [],
                },
                yearly: {
                    text: 'subscriptions.billing.yearly',
                    textValues: [plan.yearly_recurring_amount.toFixed(2)],
                    prefix_text: plan.trial_period > 0 ? 'subscriptions.free_then' : null,
                    prefix_textValues: plan.trial_period > 0 ? [plan.trial_period] : [],
                },
            };

            result = billing[plan.billing_schedule];
        }
        else if (isBefore(new Date(), new Date(plan.subscription.terminated_at))) {
            const subscription = plan.subscription;
            result = {
                text: 'subscriptions.active_until',
                textValues: [format(new Date(subscription.terminated_at), 'PPP')],
            };
        }
        else {
            const subscription = plan.subscription;
            result = {
                text: 'subscriptions.renews_on',
                textValues: [format(new Date(subscription.next_charge_on), 'PPP')],
            };
        }

        return result;
    };

    const schedule = useMemo(() => generateSchedule(), [plan]);

    return (
        <Caption textAlign={'center'}>
            <H4
                fontWeight={fontWeight}
                fontSize={fontSize}
                text={schedule.prefix_text}
                textValues={schedule.prefix_textValues}
            />
            <H4
                fontWeight={fontWeight}
                fontSize={fontSize}
                text={schedule.text}
                textValues={schedule.textValues}
            />
        </Caption>
    );
};
